// rules.jsx - Category Selector with Enhanced UI (Desktop 3-items, Mobile 1-item carousel)
import React, { useEffect, useState, useCallback, useRef } from 'react';
import './rules.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchThemeData } from '../../admin/themeSlice';
import { selectAdminToken } from '../../admin/sessionSlice';
import { setBackButtonUrl } from '../uiSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import { getArrowPuzzleForCategory } from '../../utils/arrowPuzzleGenerator';
import {
  categoriesWithPuzzles,
  isArrowCategoryPlayedFromMap,
  allArrowCategoriesCompleteFromMap,
} from '../../utils/arrowRunProgress';
import { arrowUpInitRules } from '../../functions/arrowUpStageApi';
import { setArrowGameSession } from '../arrowGameSlice';
import { fetchStageProgress } from '../../functions/fetchStageProgress';
import {
  sendReport,
  extractReportIdFromResponse,
  normalizeReportTimeValue,
  resolveReportsAuthToken,
} from '../../functions/sendReport';
import { mergeReportId } from '../loginSlice';

function getCategoryImageUrl(themeData, category) {
  if (!themeData?.cat_icons) return null;
  const iconFilename = themeData.cat_icons[category];
  if (!iconFilename) return null;
  return s3PublicUrl(iconFilename);
}

function themeSoundEnabled(themeData) {
  const v = themeData?.sound_toggle ?? themeData?.sound_toogle;
  return v === true || String(v).toLowerCase() === 'true';
}

function playThemeClick(themeData) {
  if (!themeSoundEnabled(themeData)) return;
  const raw = themeData?.default_question_sound;
  if (raw == null || String(raw).trim() === '') return;
  const url = s3PublicUrl(raw);
  if (!url || typeof Audio === 'undefined') return;
  try {
    const a = new Audio(url);
    a.volume = 0.92;
    void a.play().catch(() => {});
  } catch {
    /* ignore */
  }
}

function formatMmSs(totalSec) {
  const s = Math.max(0, Math.floor(Number(totalSec) || 0));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

const waitMs = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

function safeParseStoredUserData() {
  try {
    const raw =
      window.sessionStorage?.getItem?.("userData") ??
      window.localStorage?.getItem?.("userData") ??
      null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: themeData } = useSelector((state) => state.theme);
  const adminToken = useSelector(selectAdminToken);
  const user = useSelector((state) => state.auth.user);
  const arrowStageId = useSelector((state) => state.arrowGame.stageId);
  const arrowByCategory = useSelector((state) => state.arrowGame.byCategory);

  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [centerIndex, setCenterIndex] = useState(0);
  const initTimerRef = useRef(null);
  const prevSelectedCategoryRef = useRef(undefined);
  const reportIdRef = useRef(user?.reportId ?? null);
  const lastSyncedReportRef = useRef(null);
  const guestReportBootstrappedRef = useRef(false);
  const reportSyncInFlightRef = useRef(false);
  const reportSyncFingerprintInFlightRef = useRef(null);
  
  // Mobile detection based on window width (for rendering logic)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    prevSelectedCategoryRef.current = undefined;

    if (!themeData?.default_cat) {
      setCategories([]);
      setCenterIndex(0);
      setIsLoading(false);
      return;
    }

    const cats = Array.isArray(themeData.default_cat) ? themeData.default_cat : [];
    setCategories(cats);

    const playable = categoriesWithPuzzles(themeData);
    if (cats.length > 0) {
      const firstPlay = playable[0];
      if (firstPlay) {
        const idx = cats.findIndex((c) => String(c).toUpperCase() === firstPlay);
        setCenterIndex(idx >= 0 ? idx : 0);
      } else {
        const idxWithPuzzle = cats.findIndex((c) => {
          const up = String(c).toUpperCase();
          return getArrowPuzzleForCategory(themeData, up)?.paths?.length > 0;
        });
        setCenterIndex(idxWithPuzzle >= 0 ? idxWithPuzzle : Math.floor(cats.length / 2));
      }
    } else {
      setCenterIndex(0);
    }
    setIsLoading(false);
  }, [themeData, location.key]);

  useEffect(() => {
    if (!adminToken) return;
    const tid = typeof localStorage !== 'undefined' ? localStorage.getItem('currentTheme') : null;
    dispatch(fetchThemeData({ themeId: tid }));
  }, [dispatch, adminToken]);

  useEffect(() => {
    dispatch(setBackButtonUrl('/login?&save=true'));
  }, [dispatch]);

  useEffect(() => {
    reportIdRef.current = user?.reportId ?? null;
    if (user?.reportId != null && String(user.reportId).trim() !== '') {
      guestReportBootstrappedRef.current = true;
    }
  }, [user?.reportId]);

  const selectedCategory = categories.length && centerIndex >= 0 ? categories[centerIndex] : null;

  const syncStageToServer = useCallback(async () => {
    if (!user?.userId || !adminToken || !themeData?.themename || !selectedCategory) return;
    const defaultCat = Array.isArray(themeData.default_cat) ? themeData.default_cat : [];
    const playable = categoriesWithPuzzles(themeData);
    try {
      const data = await arrowUpInitRules({
        token: adminToken,
        userId: user.userId,
        email: user.email,
        fullName: user.name,
        user_type: user.role,
        reportid: reportIdRef.current || user.reportId,
        themeName: themeData.themename,
        selectedCategory: String(selectedCategory).toUpperCase(),
        defaultCat,
        playableCategories: playable,
      });
      dispatch(
        setArrowGameSession({
          stageId: data.stageId,
          themeName: themeData.themename,
          byCategory: data.byCategory || {},
        })
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[rules] arrowUpInitRules failed', e?.message || e);
    }
  }, [adminToken, user, themeData, selectedCategory, dispatch]);

  useEffect(() => {
    if (!themeData?.themename || !selectedCategory || !user?.userId || !adminToken) return;
    if (initTimerRef.current) clearTimeout(initTimerRef.current);

    const isFirstSelectionForSync = prevSelectedCategoryRef.current === undefined;
    prevSelectedCategoryRef.current = selectedCategory;
    const delayMs = isFirstSelectionForSync ? 0 : 280;

    initTimerRef.current = window.setTimeout(() => {
      initTimerRef.current = null;
      void syncStageToServer();
    }, delayMs);
    return () => {
      if (initTimerRef.current) clearTimeout(initTimerRef.current);
    };
  }, [themeData?.themename, selectedCategory, user?.userId, adminToken, syncStageToServer]);

  useEffect(() => {
    const syncReport = async () => {
      const persisted = safeParseStoredUserData();
      const effectiveUser = user && typeof user === "object" ? user : persisted;
      if (!effectiveUser?.userId || !effectiveUser?.sessionId || !effectiveUser?.organizationId) return;
      if (!arrowStageId || !arrowByCategory || typeof arrowByCategory !== 'object') return;

      // Read score/time from DB only. Short retry window handles eventual consistency
      // right after category save + navigation back to rules.
      let points = null;
      let totalTimeSec = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const dbProgress = await fetchStageProgress({
            userId: user.userId,
            token: adminToken,
            stageId: arrowStageId,
          });
          const st = dbProgress?.stage && typeof dbProgress.stage === 'object' ? dbProgress.stage : null;
          if (st) {
            const dbPointsRaw = Number(st.total_score ?? st.points);
            const dbTimeRaw = normalizeReportTimeValue(st.time);

            // If stage totals are missing, derive from DB arrow map (not local redux map).
            const dbMap = st.arrowByCategory && typeof st.arrowByCategory === 'object' ? st.arrowByCategory : {};
            const dbEntries = Object.values(dbMap).filter((row) => row && typeof row === 'object');
            const dbPointsFromMap = dbEntries.reduce((sum, row) => {
              const n = Number(row.score);
              return sum + (Number.isFinite(n) ? n : 0);
            }, 0);
            const dbTimeFromMap = dbEntries.reduce((sum, row) => {
              const t = normalizeReportTimeValue(row.timeSec ?? row.time);
              return sum + (Number.isFinite(t) ? t : 0);
            }, 0);

            points = Number.isFinite(dbPointsRaw) ? dbPointsRaw : dbPointsFromMap;
            totalTimeSec = Number.isFinite(dbTimeRaw) ? dbTimeRaw : dbTimeFromMap;
            if (Number.isFinite(points) && Number.isFinite(totalTimeSec)) break;
          }
        } catch {
          // try again below
        }
        if (attempt < 2) {
          await waitMs(700);
        }
      }
      if (!Number.isFinite(points) || !Number.isFinite(totalTimeSec)) {
        // Do not send mismatched local values if DB is unavailable.
        return;
      }

      const token = resolveReportsAuthToken(
        effectiveUser.token,
        `${String(effectiveUser.sessionId).trim()}&${String(effectiveUser.organizationId).trim()}`
      );
      const ridForPayload =
        reportIdRef.current != null && String(reportIdRef.current).trim() !== ''
          ? String(reportIdRef.current).trim()
          : null;
      // De-dupe by score/time only: reportId mutation after first add should not retrigger same write.
      const fingerprint = `${points}|${totalTimeSec}`;
      if (lastSyncedReportRef.current === fingerprint) return;
      if (reportSyncInFlightRef.current && reportSyncFingerprintInFlightRef.current === fingerprint) return;

      const roleUpper = String(effectiveUser.role || '').trim().toUpperCase();
      const sourceUpper = String(effectiveUser.source || '').trim().toUpperCase();
      const isGuest = roleUpper === 'GUEST_USER' || sourceUpper === 'GUEST_USER';
      const reportRole = isGuest ? 'GUEST_USER' : effectiveUser.role;
      const effectiveReportId =
        isGuest && !guestReportBootstrappedRef.current && !ridForPayload ? null : ridForPayload;
      const payload = {
        sessionId: effectiveUser.sessionId,
        role: reportRole,
        token,
        name: effectiveUser.name,
        email: effectiveUser.email || null,
        userId: effectiveUser.userId,
        gameId: effectiveUser.gameId || '',
        organizationId: effectiveUser.organizationId,
        points,
        time: totalTimeSec,
        reportId: effectiveReportId,
        ans: '',
        user_type: reportRole,
        owner: reportRole,
        guestScoreMinimal: isGuest,
      };

      try {
        reportSyncInFlightRef.current = true;
        reportSyncFingerprintInFlightRef.current = fingerprint;
        let res;
        try {
          res = await sendReport(payload);
        } catch (firstErr) {
          // If update by reportId fails (stale/missing row), create a fresh report row.
          if (ridForPayload) {
            res = await sendReport({ ...payload, reportId: null });
          } else {
            throw firstErr;
          }
        }
        const rid = extractReportIdFromResponse(res);
        if (rid && String(rid).trim() !== '') {
          const cleanRid = String(rid).trim();
          reportIdRef.current = cleanRid;
          dispatch(mergeReportId(cleanRid));
          if (isGuest) guestReportBootstrappedRef.current = true;
        }
        lastSyncedReportRef.current = fingerprint;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[rules] sendReport sync failed', e?.message || e);
      } finally {
        reportSyncInFlightRef.current = false;
        reportSyncFingerprintInFlightRef.current = null;
      }
    };

    void syncReport();
  }, [arrowByCategory, arrowStageId, user, dispatch, adminToken]);

  const handlePrevCategory = () => {
    const n = categories.length;
    if (n <= 0) return;
    setCenterIndex((i) => (i - 1 + n) % n);
  };

  const handleNextCategory = () => {
    const n = categories.length;
    if (n <= 0) return;
    setCenterIndex((i) => (i + 1) % n);
  };

  const handlePlayClick = (e) => {
    e.preventDefault();
    if (!selectedCategory) return;
    const up = String(selectedCategory).toUpperCase();
    const has = getArrowPuzzleForCategory(themeData, up)?.paths?.length > 0;
    if (!has || isArrowCategoryPlayedFromMap(up, arrowByCategory)) return;
    if (arrowStageId == null) return;
    navigate('/arrow-puzzle', { state: { category: up, stageId: arrowStageId } });
  };

  const handleAllCompleteCta = () => {
    if (arrowStageId == null) return;
    const cats = Array.isArray(themeData?.default_cat) ? themeData.default_cat.map((c) => String(c).toUpperCase()) : [];
    const scoresForThankYou = cats.slice(0, 3).map((c) => {
      const row = arrowByCategory?.[c];
      return row && Number.isFinite(Number(row.score)) ? Number(row.score) : 0;
    });
    navigate('/thankyou', {
      replace: true,
      state: {
        fromArrowRun: true,
        stageId: arrowStageId,
        energy: scoresForThankYou[0] ?? 0,
        time: scoresForThankYou[1] ?? 0,
        health: scoresForThankYou[2] ?? 0,
      },
    });
  };

  const selectedUpper = selectedCategory ? String(selectedCategory).toUpperCase() : '';
  const selectedHasPuzzle = selectedUpper && getArrowPuzzleForCategory(themeData, selectedUpper)?.paths?.length > 0;
  const selectedPlayed = selectedUpper && isArrowCategoryPlayedFromMap(selectedUpper, arrowByCategory);
  const playDisabled = !selectedCategory || !selectedHasPuzzle || selectedPlayed || arrowStageId == null;

  const allDone = themeData && allArrowCategoriesCompleteFromMap(themeData, arrowByCategory);
  const puzzleCats = themeData ? categoriesWithPuzzles(themeData) : [];

  const n = categories.length;
  const isDesktop = !isMobile;
  const isTwoCategoryDesktop = isDesktop && n === 2;
  // Build stripCats based on device: mobile shows only current category (center), desktop shows left, center, right
  let stripCats = [];
  if (n === 0) {
    stripCats = [];
  } else if (isMobile) {
    // On mobile, only show the center category (single item)
    stripCats = [{ cat: categories[centerIndex], role: 'center', ix: centerIndex }];
  } else if (isTwoCategoryDesktop) {
    // Desktop with exactly 2 categories: show both equally (no side-fade slots)
    stripCats = categories.map((cat, ix) => ({ cat, role: 'two', ix }));
  } else {
    // Desktop: show up to 3 items, but never duplicate when n < 3
    const wantIdx = [
      (centerIndex - 1 + n) % n,
      centerIndex,
      (centerIndex + 1) % n,
    ];
    const seen = new Set();
    const uniqIdx = [];
    for (const ix of wantIdx) {
      if (!seen.has(ix)) {
        seen.add(ix);
        uniqIdx.push(ix);
      }
    }
    stripCats = uniqIdx.map((ix) => ({
      cat: categories[ix],
      role: ix === centerIndex ? 'center' : 'side',
      ix,
    }));
  }

  // Compute transform offset for carousel strip (for smooth sliding)
  // For mobile: shift based on center index (but we only render 1 item, no shift needed if we just update centerIndex)
  // For desktop: we always show 3 items, the transform is based on which index is center, but since we re-render strip with correct order, we don't need transform offset.
  // However to add slide animation when changing category, we can add a simple transition on the strip container but we are replacing children.
  // For better UX, we add a key to force re-render? But we want sliding effect. Since we rebuild stripCats on index change, we can add a CSS transition on the parent strip? 
  // Instead, we'll rely on CSS transition when the component re-renders with new children. No transform needed because we are reordering the array.

  if (isLoading) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <>
      <div className="category-selector-container">
        {allDone && puzzleCats.length > 0 && (
          <div className="category-all-complete-banner" style={{ color: themeData?.text_color || '#333' }}>
            <p>🎉 You have completed every category! 🎉</p>
            <button type="button" className="play-button category-summary-cta" onClick={handleAllCompleteCta}>
              View results &amp; feedback
            </button>
          </div>
        )}

        <div className="category-carousel-wrapper">
          {!isTwoCategoryDesktop && (
            <button
              className="carousel-nav carousel-nav-prev"
              onClick={handlePrevCategory}
              aria-label="Previous category"
              type="button"
            >
              <i className="fa-solid fa-chevron-left" aria-hidden="true" />
            </button>
          )}

          <div className="category-carousel-viewport">
            <div
              className="category-carousel-strip"
              style={!isMobile && stripCats.length < 3 ? { justifyContent: 'center' } : undefined}
            >
              {stripCats.map(({ cat, role, ix }, idx) => {
                const isSelected = String(cat).toUpperCase() === selectedUpper;
                const imageUrl = getCategoryImageUrl(themeData, cat);
                const up = String(cat).toUpperCase();
                const hasPuzzle = getArrowPuzzleForCategory(themeData, up)?.paths?.length > 0;
                const played = isArrowCategoryPlayedFromMap(up, arrowByCategory);
                const result = arrowByCategory?.[up];
                const disabled = !hasPuzzle || played;
                const isCenter = role === 'center';
                const isTwoSlot = role === 'two';
                const canSelectThisCard = isTwoSlot ? !disabled : isCenter && !disabled;

                // Add additional class for side slots on desktop
                let slotClass = 'category-card--side-slot';
                if (isTwoSlot) slotClass = 'category-card--two-slot';
                else if (isCenter) slotClass = 'category-card--center-slot';

                let cardClasses = `category-card category-card--strip ${slotClass}`;
                if (isSelected) cardClasses += ' selected';
                if (disabled) cardClasses += ' category-card--disabled';
                if (played) cardClasses += ' category-card--played';

                return (
                  <div
                    key={`${cat}-${role}-${ix}-${idx}`}
                    role="button"
                    tabIndex={canSelectThisCard ? 0 : -1}
                    className={cardClasses}
                    onClick={() => {
                      if (!canSelectThisCard) return;
                      setCenterIndex(ix);
                    }}
                    onKeyDown={(ev) => {
                      if (!canSelectThisCard) return;
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        setCenterIndex(ix);
                      }
                    }}
                  >
                    <div className="category-card-image-wrapper">
                      {imageUrl && <img src={imageUrl} alt={cat} className="category-card-image" />}
                      {!hasPuzzle && <span className="category-card-badge">No puzzle</span>}
                      {played && result && (
                        <span className="category-card-badge category-card-badge--done">
                          ✅ Done · {result.score} pts · {formatMmSs(result.timeSec)}
                        </span>
                      )}
                    </div>
                    <div className="category-card-label" style={{ color: themeData?.text_color || '#333' }}>{cat}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {!isTwoCategoryDesktop && (
            <button
              className="carousel-nav carousel-nav-next"
              onClick={handleNextCategory}
              aria-label="Next category"
              type="button"
            >
              <i className="fa-solid fa-chevron-right" aria-hidden="true" />
            </button>
          )}
        </div>

     

        <div className="category-button-wrapper" >
          <button
            className="play-button"
            onClick={(e) => {
              playThemeClick(themeData);
              handlePlayClick(e);
            }}
            disabled={playDisabled}
            style={{
              backgroundColor: themeData?.box_bg_color || themeData?.box_bg_color || '#b8956a',
              color: themeData?.box_text_color || themeData?.box_text_color || '#ffffff',
              borderColor: themeData?.box_text_color || themeData?.box_text_color || '#b8956a',
              opacity: playDisabled ? 0.5 : 1,
                  width: '258px',
    position: 'relative',
    left: '25px', 
            }}
            type="button"
          >
            {selectedPlayed ? 'ALREADY PLAYED' : 'PLAY'}
          </button>
        </div>
      </div>
    </>
  );
}

export default UserRules;